import { supabase } from '../lib/supabase';
import { triggerJobRecommendationNotifications } from './notificationTriggers';

/**
 * Lazy load Gemini SDK
 * @returns {Promise<GoogleGenerativeAI>}
 */
const loadGeminiSDK = async () => {
  const { GoogleGenerativeAI } = await import('@google/generative-ai');
  return GoogleGenerativeAI;
};

/**
 * Get Gemini AI instance
 * @returns {Promise<GoogleGenerativeAI>}
 */
const getGeminiAI = async () => {
  const GoogleGenerativeAI = await loadGeminiSDK();
  return new GoogleGenerativeAI(import.meta.env.VITE_GEMINI_API_KEY);
};

/**
 * Job Recommendations Service with Gemini AI Integration
 * Provides AI-powered job matching and personalized recommendations
 */

/**
 * Converts snake_case to camelCase
 */
const toCamelCase = (obj) => {
  if (Array.isArray(obj)) {
    return obj?.map(toCamelCase);
  }
  if (obj !== null && typeof obj === 'object') {
    return Object.keys(obj)?.reduce((acc, key) => {
      const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
      acc[camelKey] = toCamelCase(obj?.[key]);
      return acc;
    }, {});
  }
  return obj;
};

/**
 * Converts camelCase to snake_case
 */
const toSnakeCase = (obj) => {
  if (Array.isArray(obj)) {
    return obj?.map(toSnakeCase);
  }
  if (obj !== null && typeof obj === 'object') {
    return Object.keys(obj)?.reduce((acc, key) => {
      const snakeKey = key?.replace(/[A-Z]/g, letter => `_${letter?.toLowerCase()}`);
      acc[snakeKey] = toSnakeCase(obj?.[key]);
      return acc;
    }, {});
  }
  return obj;
};

/**
 * Generate AI-powered job recommendations using Gemini
 */
export async function generateJobRecommendations(userId, limit = 10) {
  try {
    // Fetch user profile and preferences
    const { data: profile, error: profileError } = await supabase?.from('user_profiles')?.select(`
        *,
        work_experience (*),
        education (*),
        skills (*),
        job_preferences (*)
      `)?.eq('user_id', userId)?.single();

    if (profileError) throw profileError;

    // Fetch user's job interactions for behavioral data
    const { data: interactions, error: interactionsError } = await supabase?.from('user_job_interactions')?.select('job_id, interaction_type, interaction_metadata')?.eq('user_id', userId)?.order('created_at', { ascending: false })?.limit(50);

    if (interactionsError) throw interactionsError;

    // Fetch active jobs that user hasn't applied to or saved
    const { data: appliedJobs } = await supabase?.from('job_applications')?.select('job_id')?.eq('user_id', userId);

    const { data: savedJobs } = await supabase?.from('saved_jobs')?.select('job_id')?.eq('user_id', userId);

    const excludedJobIds = [
      ...(appliedJobs?.map(j => j?.job_id) || []),
      ...(savedJobs?.map(j => j?.job_id) || [])
    ];

    // Fetch available jobs
    let jobsQuery = supabase?.from('jobs')?.select('*')?.eq('status', 'active')?.order('created_at', { ascending: false })?.limit(100);

    if (excludedJobIds?.length > 0) {
      jobsQuery = jobsQuery?.not('id', 'in', `(${excludedJobIds?.join(',')})`);
    }

    const { data: availableJobs, error: jobsError } = await jobsQuery;
    if (jobsError) throw jobsError;

    if (!availableJobs || availableJobs?.length === 0) {
      return [];
    }

    // Prepare data for AI matching
    const userContext = {
      profile: {
        title: profile?.current_title,
        experienceYears: profile?.years_of_experience,
        location: profile?.location,
        remote: profile?.open_to_remote,
        relocation: profile?.willing_to_relocate,
      },
      skills: profile?.skills?.map(s => s?.skill_name) || [],
      experience: profile?.work_experience?.map(exp => ({
        title: exp?.job_title,
        company: exp?.company_name,
        duration: `${exp?.start_date} - ${exp?.end_date || 'Present'}`,
      })) || [],
      preferences: profile?.job_preferences?.[0] || {},
      recentInteractions: interactions?.slice(0, 10) || [],
    };

    // Use Gemini to analyze and score job matches
    const recommendations = await analyzeJobMatchesWithGemini(userContext, availableJobs, limit);

    // Store recommendations in database
    const recommendationsToStore = recommendations?.map(rec => toSnakeCase({
      userId,
      jobId: rec?.jobId,
      matchScore: rec?.matchScore,
      matchReasons: rec?.matchReasons,
      aiExplanation: rec?.aiExplanation,
      isShown: false,
    }));

    const { data: storedRecs, error: storeError } = await supabase?.from('job_recommendations')?.upsert(recommendationsToStore, {
        onConflict: 'user_id,job_id',
        ignoreDuplicates: false,
      })?.select();

    if (storeError) throw storeError;

    // Trigger notification for new recommendations
    try {
      // Enhance recommendations with job details for notification
      const recsWithJobDetails = await Promise.all(
        recommendations?.map(async (rec) => {
          const { data: job } = await supabase?.from('jobs')?.select('title, companies(name)')?.eq('id', rec?.jobId)?.single();
          
          return {
            ...rec,
            jobTitle: job?.title || 'Job',
            companyName: job?.companies?.name || 'Company'
          };
        })
      );

      await triggerJobRecommendationNotifications(userId, recsWithJobDetails);
    } catch (notificationError) {
      console.error('Failed to trigger recommendation notifications:', notificationError);
      // Don't fail the recommendation generation if notification fails
    }

    return toCamelCase(storedRecs);
  } catch (error) {
    console.error('Error generating job recommendations:', error);
    throw error;
  }
}

/**
 * Analyze job matches using Gemini AI
 */
async function analyzeJobMatchesWithGemini(userContext, jobs, limit) {
  try {
    const model = await getGeminiAI();
    const prompt = `
You are an expert career advisor and job matching AI. Analyze the following user profile and job listings to generate personalized job recommendations.

User Profile:
${JSON.stringify(userContext, null, 2)}

Available Jobs (${jobs?.length} listings):
${JSON.stringify(jobs?.slice(0, 20), null, 2)}

Task: Analyze each job and provide:
1. A match score (0-100) based on:
   - Skills alignment (40%)
   - Experience level fit (25%)
   - Location/remote preference (15%)
   - Job type and salary expectations (20%)
2. Specific reasons for the match (3-5 key points)
3. A brief personalized explanation (2-3 sentences)

Return ONLY a valid JSON array of the top ${limit} recommendations in this exact format:
[
  {
    "jobId": "uuid-here",
    "matchScore": 95,
    "matchReasons": [
      {"category": "Skills", "explanation": "Strong match with React, Node.js, and AWS"},
      {"category": "Experience", "explanation": "Your 5 years aligns perfectly with their senior role"},
      {"category": "Location", "explanation": "Remote-first company matching your preference"}
    ],
    "aiExplanation": "This role is an excellent fit because your React expertise and AWS experience directly match their tech stack. The fully remote position aligns with your preferences, and the salary range meets your expectations."
  }
]

Important: Return ONLY the JSON array, no additional text or explanations.
`;

    const result = await model?.generateContent(prompt);
    const response = await result?.response;
    const text = response?.text();

    // Extract JSON from response
    const jsonMatch = text?.match(/\[[\s\S]*\]/);
    if (!jsonMatch) {
      throw new Error('Failed to parse AI response');
    }

    const recommendations = JSON.parse(jsonMatch?.[0]);
    return recommendations?.slice(0, limit);
  } catch (error) {
    console.error('Error in Gemini job matching:', error);
    
    // Fallback to basic matching if AI fails
    return jobs?.slice(0, limit)?.map(job => ({
      jobId: job?.id,
      matchScore: 70,
      matchReasons: [
        { category: 'Skills', explanation: 'Basic skills alignment detected' },
        { category: 'Location', explanation: 'Location matches your preferences' },
      ],
      aiExplanation: 'This job matches your basic criteria and is worth considering.',
    }));
  }
}

/**
 * Get top recommendations for a user
 */
export async function getTopRecommendations(userId, limit = 10) {
  try {
    const { data, error } = await supabase?.rpc('get_top_recommendations', {
        p_user_id: userId,
        p_limit: limit,
      });

    if (error) throw error;
    return toCamelCase(data);
  } catch (error) {
    console.error('Error fetching recommendations:', error);
    throw error;
  }
}

/**
 * Mark recommendations as shown
 */
export async function markRecommendationsAsShown(recommendationIds) {
  try {
    const { data, error } = await supabase?.from('job_recommendations')?.update({ is_shown: true, shown_at: new Date()?.toISOString() })?.in('id', recommendationIds)?.select();

    if (error) throw error;
    return toCamelCase(data);
  } catch (error) {
    console.error('Error marking recommendations as shown:', error);
    throw error;
  }
}

/**
 * Subscribe to real-time job recommendation updates
 * @param {string} userId - User ID to subscribe to
 * @param {function} callback - Callback function to handle updates
 * @returns {function} Unsubscribe function
 */
export const subscribeToJobRecommendations = (userId, callback) => {
  const channel = supabase
    ?.channel('job-recommendations-changes')
    ?.on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'job_recommendations',
        filter: `user_id=eq.${userId}`
      },
      async (payload) => {
        const eventType = payload?.eventType;
        const recommendation = payload?.new || payload?.old;

        if (recommendation) {
          // Fetch job details for the recommendation
          let jobDetails = null;
          if (eventType === 'INSERT' || eventType === 'UPDATE') {
            const { data: job } = await supabase
              ?.from('jobs')
              ?.select(`
                id,
                title,
                description,
                employment_type,
                experience_level,
                salary_min,
                salary_max,
                companies (
                  id,
                  name,
                  logo_url
                )
              `)
              ?.eq('id', recommendation?.job_id)
              ?.single();

            if (job) {
              jobDetails = {
                id: job?.id,
                title: job?.title,
                description: job?.description,
                employmentType: job?.employment_type,
                experienceLevel: job?.experience_level,
                salaryMin: job?.salary_min,
                salaryMax: job?.salary_max,
                company: {
                  id: job?.companies?.id,
                  name: job?.companies?.name,
                  logoUrl: job?.companies?.logo_url
                }
              };
            }
          }

          callback({
            eventType,
            recommendation: {
              id: recommendation?.id,
              userId: recommendation?.user_id,
              jobId: recommendation?.job_id,
              matchScore: recommendation?.match_score,
              matchReasons: recommendation?.match_reasons,
              aiExplanation: recommendation?.ai_explanation,
              isShown: recommendation?.is_shown,
              createdAt: recommendation?.created_at,
              job: jobDetails
            }
          });
        }
      }
    )
    ?.subscribe();

  // Return unsubscribe function
  return () => {
    supabase?.removeChannel(channel);
  };
};

/**
 * Subscribe to job recommendation feedback updates
 * @param {string} userId - User ID to subscribe to
 * @param {function} callback - Callback function to handle updates
 * @returns {function} Unsubscribe function
 */
export const subscribeToRecommendationFeedback = (userId, callback) => {
  const channel = supabase
    ?.channel('recommendation-feedback-changes')
    ?.on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'recommendation_feedback',
        filter: `user_id=eq.${userId}`
      },
      (payload) => {
        const feedback = payload?.new;

        if (feedback) {
          callback({
            id: feedback?.id,
            recommendationId: feedback?.recommendation_id,
            userId: feedback?.user_id,
            feedbackType: feedback?.feedback_type,
            feedbackReason: feedback?.feedback_reason,
            createdAt: feedback?.created_at
          });
        }
      }
    )
    ?.subscribe();

  // Return unsubscribe function
  return () => {
    supabase?.removeChannel(channel);
  };
};

/**
 * Submit recommendation feedback
 */
export async function submitRecommendationFeedback(recommendationId, feedbackType, reason = null) {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('User not authenticated');

    const feedbackData = toSnakeCase({
      recommendationId,
      userId: user?.id,
      feedbackType,
      feedbackReason: reason,
    });

    const { data, error } = await supabase?.from('recommendation_feedback')?.insert(feedbackData)?.select()?.single();

    if (error) throw error;
    return toCamelCase(data);
  } catch (error) {
    console.error('Error submitting feedback:', error);
    throw error;
  }
}

/**
 * Track job interaction
 */
export async function trackJobInteraction(jobId, interactionType, metadata = {}) {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase?.rpc('track_job_interaction', {
      p_user_id: user?.id,
      p_job_id: jobId,
      p_interaction_type: interactionType,
      p_metadata: metadata,
    });

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error tracking interaction:', error);
    throw error;
  }
}

/**
 * Get or create digest preferences
 */
export async function getDigestPreferences(userId) {
  try {
    let { data, error } = await supabase?.from('digest_preferences')?.select('*')?.eq('user_id', userId)?.single();

    // If no preferences exist, create default ones
    if (error && error?.code === 'PGRST116') {
      const defaultPrefs = toSnakeCase({
        userId,
        isEnabled: true,
        frequency: 'weekly',
        preferredDayOfWeek: 1, // Monday
        preferredTime: '09:00:00',
      });

      const { data: newData, error: insertError } = await supabase?.from('digest_preferences')?.insert(defaultPrefs)?.select()?.single();

      if (insertError) throw insertError;
      data = newData;
    } else if (error) {
      throw error;
    }

    return toCamelCase(data);
  } catch (error) {
    console.error('Error fetching digest preferences:', error);
    throw error;
  }
}

/**
 * Update digest preferences
 */
export async function updateDigestPreferences(userId, preferences) {
  try {
    const updates = toSnakeCase(preferences);

    const { data, error } = await supabase?.from('digest_preferences')?.update(updates)?.eq('user_id', userId)?.select()?.single();

    if (error) throw error;
    return toCamelCase(data);
  } catch (error) {
    console.error('Error updating digest preferences:', error);
    throw error;
  }
}

/**
 * Get recommendation statistics for user
 */
export async function getRecommendationStats(userId) {
  try {
    const { data, error } = await supabase?.from('job_recommendations')?.select('match_score, is_shown, created_at')?.eq('user_id', userId);

    if (error) throw error;

    const stats = {
      totalRecommendations: data?.length,
      averageMatchScore: data?.reduce((sum, rec) => sum + parseFloat(rec?.match_score), 0) / data?.length || 0,
      shownCount: data?.filter(rec => rec?.is_shown)?.length,
      newCount: data?.filter(rec => !rec?.is_shown)?.length,
      lastGenerated: data?.length > 0 ? new Date(Math.max(...data.map(rec => new Date(rec.created_at))))?.toISOString() : null,
    };

    return stats;
  } catch (error) {
    console.error('Error fetching recommendation stats:', error);
    throw error;
  }
}

export default {
  generateJobRecommendations,
  getTopRecommendations,
  markRecommendationsAsShown,
  submitRecommendationFeedback,
  trackJobInteraction,
  getDigestPreferences,
  updateDigestPreferences,
  getRecommendationStats,
  subscribeToJobRecommendations,
  subscribeToRecommendationFeedback
};